<template>
  <div
    v-click-outside="closeDropdown"
    class="mdt-datepicker"
    :data-test="testKey"
    :class="{ opened: isDropdownOpen }">
    <div
      class="datepicker-input"
      :class="{ disabled: disabled }">
      <MdtInput
        :id="id"
        ref="inputEl"
        v-model="inputValue"
        :label="label"
        :placeholder="format"
        :required="required"
        :readonly="readonly"
        :disabled="disabled"
        :type="'date'"
        :size="size"
        :hide-optional="hideOptional"
        :errors="errors"
        :tooltip="tooltip"
        :max-date="maxDate"
        :min-date="minDate"
        :test-key="testKey"
        @input="setInput"
        @clickInput="onToggleDropdown">
        <i
          slot="icon"
          class="fas fa-calendar-day icon no-pointerevents"
          :class="{ selected: selected.day }" />
      </MdtInput>
    </div>
    <div
      v-if="isDropdownOpen"
      ref="scroll"
      class="datepicker-wrapper"
      :class="[
        position, {
          relative,
          'right-aligned': rightAligned,
          'has-selection': helpers.isNotUndefined(selected.day),
        }]">
      <div class="datepicker-header flex-center-v">
        <div class="datepicker-current-date">
          <MdtSelect
            ref="months"
            v-model="monthOptions.value"
            class="months-options"
            zero-value-allowed
            min-width="115px"
            required
            :show-clear-search="false"
            :options="monthOptions.items"
            @input="onMonthSet" />
          <MdtSelect
            ref="years"
            v-model="yearOptions.value"
            class="years-options"
            zero-value-allowed
            min-width="80px"
            required
            :options="yearOptions.items"
            :show-clear-search="false"
            @input="onYearSet" />
        </div>
        <div class="months-buttons">
          <div
            id="previous-month"
            class="icon"
            :class="{ disabled: disablePastYear }"
            @click="setMonth(current.month - 1)">
            <i class="fas fa-angle-left" />
          </div>
          <div
            id="next-month"
            class="icon"
            :class="{ disabled: disableFutureYear }"
            @click="setMonth(current.month + 1)">
            <i class="fas fa-angle-right" />
          </div>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th
              v-for="(weekday, i) in weekdays"
              :key="i">
              {{ weekday }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(days, index) in calendar"
            :key="index">
            <td
              v-for="(day, d) in days"
              :id="`day-${day.day}`"
              :key="d"
              :class="{
                current: day.current,
                disabled: !day.day || !allowPastDates && day.past,
                selected: day.selected,
              }"
              @click.stop="setDay(day)">
              {{ day.day }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
/* Much more simplified version than: https://github.com/ridewn/vue-date-picker/blob/master/src/DatePicker.vue */
import { mixinToggleDropdown } from '@/mixins';
import { helpers, array } from '@/utility';

export default {
  name: 'MdtDatepicker',
  mixins: [mixinToggleDropdown],
  props: {
    value: {
      type: [String, Date, Object],
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    format: {
      type: String,
      default: 'DD.MM.YYYY',
    },
    position: {
      type: String,
      default: 'bottom',
      validator: (value) => ['bottom', 'top'].includes(value),
    },
    rightAligned: {
      type: Boolean,
      default: false,
    },
    allowPastDates: {
      type: Boolean,
      default: true,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    relative: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'size-40',
      validator: (value) => {
        const match = ['size-40', 'size-32'];
        return match.includes(value);
      },
    },
    hideOptional: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: String,
      default: '',
    },
    id: {
      type: [String, Number],
      default: () => helpers.generateUniqueId(),
    },
    maxDate: {
      type: Object,
      default: () => ({
        date: null,
        error_msg: '',
      }),
    },
    minDate: {
      type: Object,
      default: () => ({
        date: null,
        error_msg: '',
      }),
    },
    testKey: {
      type: String,
      default: '',
    },
  },
  data() {
    // Months: zero-based (0 = January, 11 = December)
    const today = moment();
    const weekdays = [...moment().locale(this.$store.state.language).localeData().weekdaysMin()];
    // Move Sunday to last place
    weekdays.push(weekdays.shift());

    return {
      helpers,
      months: moment().locale(this.$store.state.language).localeData().months(),
      weekdays,
      current: {
        year: today.year(),
        month: today.month(),
        day: today.date(),
      },
      selected: {
        year: '',
        month: '',
        day: '',
        dayOfWeek: '',
      },
      monthOptions: {
        items: [],
        value: '',
      },
      yearOptions: {
        items: [],
        value: '',
      },
      inputValue: '',
      serverErrors: this.errors || [],
      initialYear: today.year(),
      MAX_FUTURE_YEARS: 5,
      MIN_PAST_YEARS: 100,
    };
  },
  computed: {
    calendar() {
      const currentMonthDate = moment(new Date(this.current.year, this.current.month, 1));
      const days = [];
      const startOfMonthDay = currentMonthDate.day() === 0 ? 7 : currentMonthDate.day();
      for (let i = 0; i < startOfMonthDay - 1; i++) {
        days.push('');
      }

      const daysInMonth = currentMonthDate.daysInMonth();
      for (let i = 1; i < daysInMonth + 1; i++) {
        days.push(i);
      }

      const dayObjects = days.map((day) => {
        const date = day ? new Date(this.current.year, this.current.month, day) : null;
        const current = moment().isSame(date, 'day');
        const past = moment().isAfter(date, 'day');
        const selected = (this.selected.day === day)
          && (this.current.month === this.selected.month)
          && (this.current.year === this.selected.year);

        return {
          day,
          current,
          past,
          selected,
        };
      });

      return array.chunk(dayObjects, 7);
    },
    disablePastYear() {
      const { month, year } = this.current;
      return month === 0 && year === this.initialYear - this.MIN_PAST_YEARS;
    },
    disableFutureYear() {
      const { month, year } = this.current;
      return month === 11 && year === this.initialYear + this.MAX_FUTURE_YEARS;
    },
  },
  watch: {
    isDropdownOpen(value) {
      if (value) {
        this.$nextTick(() => {
          this.scrollTheView();
        });
      }
    },
    value(value) {
      if (!value) this.inputValue = '';
    },
    errors(value) {
      this.serverErrors = value;
    },
  },
  mounted() {
    // set initial month options
    this.setMonthOptions();
    // set initial year options
    this.setYearOptions();

    if (!this.value) return;

    // Format date to moment input type
    let date = this.value;
    if (typeof this.value === 'string') {
      const splitKey = date.includes('.') ? '.' : '/';
      date = date.split(splitKey).reverse().join('-');
    }

    date = moment(date);
    if (!date.isValid()) return;

    this.current.year = date.year();
    this.current.month = date.month();
    this.current.day = date.date();
    this.setDay({ day: this.current.day }, 'init');
  },
  methods: {
    isValid() {
      if (this.required) return this.$refs.inputEl.isValid() && this.isDateValid(this.inputValue);
      return this.$refs.inputEl.isValid();
    },
    isDateValid(date) {
      date = moment(date, 'DD.MM.YYYY', true);
      return date.isValid();
    },
    setInput(value) {
      let date = value;

      // if input date is not null and date is not valid -> handle it properly and return
      if (this.helpers.isNotUndefined(date) && !this.isDateValid(date)) {
        this.onInvalidInput(date);
        return;
      }

      // if date is undefined -> handle dates properly
      if (this.helpers.isUndefined(date)) {
        const today = moment();
        this.current = {
          year: today.year(),
          month: today.month(),
          day: today.date(),
        };
        this.yearOptions.value = today.year();
        this.monthOptions.value = today.month();
        this.inputValue = '';
        this.setDay({ day: null }, false, true);
      } else { // if date is not undefined -> handle dates properly
        // Format date to moment input type
        date = moment(date, 'DD.MM.YYYY', true);

        this.yearOptions.value = date.year();
        this.monthOptions.value = date.month();
        this.current.year = date.year();
        this.current.month = date.month();
        this.current.day = date.date();
        this.setDay({ day: this.current.day });
      }
    },
    onInvalidInput(date) {
      this.selected.day = null;
      this.inputValue = date;
      this.$emit('onSelectedInput', this.inputValue);
      this.$emit('mdtDataChanged');
    },
    setMonthOptions() {
      const monthItems = this.months.map((month, index) => ({
        value: index,
        displayName: month,
      }));

      this.monthOptions = {
        items: monthItems,
        value: this.current.month,
      };
    },
    setYearOptions() {
      const { year } = this.current;
      const years = [];

      for (let i = year - this.MIN_PAST_YEARS; i < year; i++) {
        years.push({ displayName: i.toString(), value: i });
      }

      for (let i = year; i <= year + this.MAX_FUTURE_YEARS; i++) {
        years.push({ displayName: i.toString(), value: i });
      }

      this.yearOptions = {
        items: years,
        value: year,
      };
    },
    onMonthSet(value) {
      this.current.month = value;
    },
    onYearSet(value) {
      this.current.year = value;
    },
    setMonth(month) {
      if (month === 12) {
        if (this.disableFutureYear) return;
        this.monthOptions.value = 0; // January
        this.yearOptions.value += 1; // Next year
        this.current.year += 1; // Next year
        this.current.month = 0; // January
        return;
      }

      if (month === -1) {
        if (this.disablePastYear) return;
        this.monthOptions.value = 11; // December
        this.yearOptions.value -= 1; // Previous year
        this.current.year -= 1; // Previous year
        this.current.month = 11; // December
        return;
      }

      this.monthOptions.value = month;
      this.current.month = month;
    },
    setDay(day, init, isEmptyDate) {
      // Prevent setting past days if not allowed
      if (!this.allowPastDates && day.past) return;

      this.selected.year = this.current.year;
      this.selected.month = this.current.month;
      this.selected.day = day.day;
      this.selected.dayOfWeek = new Date(
        this.selected.year,
        this.selected.month,
        day.day,
      ).getDay();
      this.$emit('onSelected', this.selected);

      let date = '';

      // when is not empty date called from setInput -> format date and inputValue properly
      if (!isEmptyDate) {
        date = new Date(this.selected.year, this.selected.month, this.selected.day);
        this.inputValue = moment(date).format(this.format);
      } else { // on empty date clear inputValue
        this.inputValue = '';
      }

      this.$emit('input', date);
      this.$emit('onSelectedInput', this.inputValue);

      if (!init) {
        // emit mdtDataChanged event so changes could be detected
        this.$emit('mdtDataChanged');
      }

      this.closeDropdown();
    },
    scrollTheView() {
      const { scroll } = this.$refs;
      if (scroll) {
        // scroll the view if neccessary to show whole component
        scroll.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    },
    onToggleDropdown() {
      if (this.disabled || this.readonly) return;
      this.toggleDropdown();
    },
  },
};
</script>

<style lang="scss" scoped>
.mdt-datepicker {
  position: relative;
  font-size: 14px;

  ::v-deep .input-wrapper {
    width: 100%;
    height: 100%;
    min-width: 145px;
    background-color: $color-white;

    input {
      cursor: pointer;
    }

    .icon.selected {
      color: $color-text-secondary;
    }
  }

  .datepicker-input.disabled {
    ::v-deep .input-wrapper {
      input {
        cursor: default;
      }
    }
  }
}

.datepicker-wrapper {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 314px;
  min-width: 314px;
  max-width: 314px;
  padding: 30px;
  box-shadow: 0px 2px 20px 0px #0000002a;
  border-radius: 4px;
  background-color: $color-back-primary;
  z-index: 101;

  &.right-aligned {
    left: auto;
    right: 0;
  }

  &.top {
    top: auto;
    bottom: calc(100% + 1px);
  }

  &.relative {
    position: relative;
    top: auto;
    margin-top: 4px;

    &.top {
      margin-bottom: 4px;
    }
  }

  &.has-selection {
    & > table {
      tbody td {
        &.current:not(.selected) {
          color: $color-theme !important;
          background-color: rgba($color-theme-rgb, 0.1) !important;
        }
      }
    }
  }

  &:not(.has-selection) {
    & > table {
      tbody td {
        &.current {
          color: $color-theme !important;
          background-color: rgba($color-theme-rgb, 0.1) !important;
          border: 1px solid $color-theme;
        }
      }
    }
  }

  .datepicker-header {
    justify-content: space-between;
    flex-wrap: nowrap;
    padding-bottom: 12px;

    .months-options {
      margin-right: 8px;

      ::v-deep .select-wrapper {
        min-width: initial;

        .search {
          max-width: 115px;
        }

        .select-dropdown {
          min-width: 115px;
        }
      }
    }

    .years-options {
      ::v-deep .select-wrapper {
        min-width: initial;

        .search {
          max-width: 80px;
        }

        .select-dropdown {
          min-width: 80px;
        }
      }
    }

    .datepicker-current-date {
      display: flex;
      color: $color-text-primary;
      font-size: 18px;
      font-weight: $font-weight-bold;
    }

    .months-buttons {
      display: flex;
      gap: 12px;

      & .disabled {
        opacity: 0.3;
      }

      .icon {
        color: $color-text-secondary;
        cursor: pointer;

        &:first-child {
          margin-right: 8px;
        }
      }
    }
  }

  & > table {
    width: 100%;
    padding: 0;
    border-spacing: 0px;
    text-align: center;
    text-transform: uppercase;

    tr {
      white-space: initial;
    }

    th, td {
      width: 36px;
      height: 36px;
      font-size: 14px;
      font-weight: $font-weight-normal;
    }

    thead th {
      color: $color-text-secondary;
    }

    tbody td {
      color: $color-text-primary;
      border-radius: 4px;
      cursor: pointer;

      &:hover {
        background-color: rgba($color-text-secondary-rgb, 0.1);
      }

      &.current {
        color: $color-theme;
        background-color: rgba($color-theme-rgb, 0.1);
        font-weight: $font-weight-bold;
      }

      &.disabled {
        color: rgba($color-text-secondary-rgb, 0.5);
        cursor: default;
        pointer-events: none;
      }

      &.selected {
        color: $color-white;
        background-color: $color-theme;
      }
    }
  }
}
</style>
